import '../App.css';
import { useEffect, useRef, useState, ReactElement } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';

import { library } from '@fortawesome/fontawesome-svg-core';
import { fontLibrary } from '../fonts';
import { useAppDispatch, useAppSelector, useInactivityTimer, useUser } from '../hooks';
import { showErrorMessage } from '../../components/message-boxes/ErrorMessage';
import { useApiContext } from '../../api';
import { setTemplates } from '../../data/templateSlice';
import { ErrorPage } from '../../components/error-boundary/ErrorPage';
import { selectTaskExecution } from '../../data/taskExecutionSlice';
import { applyDefaultTheme } from '../../features/themes/themes';
import { setTaskDefinitions } from '../../data/taskDefinitionSlice';
import {
  addPollingConfiguration,
  removePollingConfiguration,
  selectPollingConfigurations,
  setIsPolling,
} from '../../data/pollingSlice';
import { updatePolling } from '../../data/pollingService';
import { promptAcceptEula } from '../../features/settings/elements/AcceptEula';
import { setEtlComponentTypesFromTemplates } from '../../features/etl-pipeline/etl-component-types/factory';
import { authenticationHealthCheck } from '../../api/healthcheck/auth-healthcheck';
import { promptCountDownBox } from '../../components/message-boxes/CountDownbox';
import { countDownDataApp, pollingConfigApp } from '../utils/app-helpers';
import PlatformAppClient from '../utils/PlatformAppClient';
import LoadingScreen from '../utils/LoadingScreen';

const EULA_VERSION = import.meta.env.VITE_EULA_VERSION;

function App(): ReactElement {
  // unpack list of fonts into FontaWesome's library
  library.add(...fontLibrary);

  const auth0 = useAuth0();
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const pollingConfig = useAppSelector(selectPollingConfigurations);
  const taskExecutions = useAppSelector(selectTaskExecution);
  const { user, setUser } = useUser();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const onLoadError = useRef(null);

  applyDefaultTheme();

  useEffect(() => {
    // Only do once at start-up
    // Ping identity provider.
    authenticationHealthCheck()
      .then((message) => console.log(message))
      .catch((error) => console.error(error));
  }, []);

  const handleInactivity = () => {
    promptCountDownBox(countDownDataApp)
      .then(() => {})
      .catch(() => {
        api.logout(true);
      });
  };
  useInactivityTimer(handleInactivity);

  const initializeApp = () => {
    api.user
      .fetchUser()
      .then((apiData) => {
        const userData = auth0.user;
        if (userData!.email!.toLowerCase() !== apiData.email!.toLowerCase()) {
          // If a sessionId was not properly removed fram a previous user session, the user will be logged out
          api.logout(true);
          return Promise.reject('invalid user session');
        }
        setUser({
          email: userData!.email!.toLowerCase(),
          name: userData!.nickname!,
          avatar: userData!.picture!,
          isOrganizationAdmin: apiData.isOrganizationAdmin,
          canWrite: apiData.canWrite,
          acceptedEulaVersion: apiData.acceptedEulaVersion,
          organizations: apiData.organizations,
          isSystemAdmin: apiData.isSystemAdmin,
        });
        return api.template.fetchTemplates();
      })
      .then((templates) => {
        // store the ontology in the redux store
        return dispatch(setTemplates({ templates }));
      })
      .then(() => {
        // Fetch the external ETL component types
        return api.etl.fetchComponentTypeTemplates();
      })
      .then((etlComponentTypeTemplates) => {
        setEtlComponentTypesFromTemplates(etlComponentTypeTemplates);
        return api.task.fetchTaskDefinitions();
      })
      .then((taskDefinitions) => {
        setIsLoaded(true);
        setStartLoading(false);
        dispatch(setTaskDefinitions({ taskDefinitions }));
      })
      .then(() => {
        return api.task.fecthAndStoreTaskExecutions();
      })
      .catch((error) => {
        onLoadError.current = error;
        setStartLoading(false);
        return;
      });
  };

  useEffect(() => {
    if (isAuthenticated && !isLoaded) {
      setStartLoading(true);
      initializeApp();
    }
  }, [isAuthenticated, isLoaded]);

  useEffect(() => {
    if (isAuthenticated) {
      setIsLoaded(false);
    }
  }, [user.organizations]);

  useEffect(() => {
    if (!auth0.isLoading && !auth0.isAuthenticated) {
      auth0.loginWithRedirect();
    }
  }, [auth0.isLoading]);

  useEffect(() => {
    if (isLoaded && Object.keys(user).length > 0 && user.acceptedEulaVersion !== EULA_VERSION) {
      promptAcceptEula(api)
        .then(() => {
          setUser({
            ...user,
            acceptedEulaVersion: EULA_VERSION,
          });
        })
        .catch((error) => {
          console.error(error);
          api.logout(true);
        });
    }
  }, [user, isLoaded]);

  useEffect(() => {
    if (onLoadError.current) {
      showErrorMessage((onLoadError.current as Error).message);
    }
  }, [onLoadError, isLoaded]);

  useEffect(() => {
    if (auth0.isAuthenticated) {
      setIsAuthenticated(true);
    }
  }, [auth0.isAuthenticated]);

  useEffect(() => {
    if (taskExecutions.taskExecutionList.length === 0) return;
    if (taskExecutions.taskExecutionList.some((task) => task.status === 'running' || task.status === 'queued')) {
      dispatch(addPollingConfiguration(pollingConfigApp));
    } else {
      dispatch(removePollingConfiguration({ pollingConfigId: pollingConfigApp.identifier }));
    }
  }, [taskExecutions]);

  useEffect(() => {
    if (pollingConfig.isPolling && pollingConfig.pollingConfigurations.length === 0) {
      dispatch(setIsPolling(false));
    } else {
      updatePolling(api, dispatch, pollingConfig);
    }
  }, [pollingConfig]);

  if (onLoadError.current) {
    return <ErrorPage error={onLoadError.current} />;
  } else if (isLoaded) {
    // Load the actual application
    return <PlatformAppClient {...user} />;
  } else if (startLoading) {
    return <LoadingScreen />;
  }

  return <></>;
}

export default withAuthenticationRequired(App);
